/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react';
import { Form, Button, Stack } from 'react-bootstrap';
import { getMetadata, setMetadata } from '../../data';
import { StatusContext } from '../StatusContext';
import { SettingsContext } from '../SettingsContext';
import { useTheme } from 'styled-components';

function MetadataSettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const [metadataInfo, setMetadataInfo] = useState<string>('');
    const [metadataSerial, setMetadataSerial] = useState<string>('');
    const [metadataTitle, setMetadataTitle] = useState<string>('');
    const [metadataBranch, setMetadataBranch] = useState<string>('');
    const [metadataGitHash, setMetadataGitHash] = useState<string>('');
    const [metadataLastCommitMsg, setMetadataLastCommitMsg] = useState<string>('');
    const [savedMetadataInfo, setSavedMetadataInfo] = useState<string>('');
    const [savedMetadataSerial, setSavedMetadataSerial] = useState<string>('');
    const [savedMetadataTitle, setSavedMetadataTitle] = useState<string>('');
    const [savedMetadataBranch, setSavedMetadataBranch] = useState<string>('');
    const [savedMetadataGitHash, setSavedMetadataGitHash] = useState<string>('');
    const [savedMetadataLastCommitMsg, setSavedMetadataLastCommitMsg] = useState<string>('');
    const [refresh, setRefresh] = useState<number>(0);
    const statusContext = useContext(StatusContext);
    const settingsContext = useContext(SettingsContext);

    const dirty = metadataInfo !== savedMetadataInfo || metadataSerial !== savedMetadataSerial ||
                  metadataTitle !== savedMetadataTitle || metadataBranch !== savedMetadataBranch ||
                  metadataGitHash !== savedMetadataGitHash || metadataLastCommitMsg !== savedMetadataLastCommitMsg;

    useEffect(() => {
        settingsContext.setIsDirty(dirty);
    }, [dirty, settingsContext]);

    useEffect(() => {
        async function load() {
            try {
                const meta = await getMetadata();
                setMetadataInfo(meta.info || '');
                setMetadataSerial(meta.serial || '');
                setMetadataTitle(meta.title || '');
                setMetadataBranch(meta.branch || '');
                setMetadataGitHash(meta.git_hash || '');
                setMetadataLastCommitMsg(meta.last_commit_msg || '');
                setSavedMetadataInfo(meta.info || '');
                setSavedMetadataSerial(meta.serial || '');
                setSavedMetadataTitle(meta.title || '');
                setSavedMetadataBranch(meta.branch || '');
                setSavedMetadataGitHash(meta.git_hash || '');
                setSavedMetadataLastCommitMsg(meta.last_commit_msg || '');
            } catch (err: any) {
                console.error('Error loading metadata:', err);
            }
        }
        load();
    }, [refresh]);

    async function save() {
        try {
            await setMetadata(metadataInfo, metadataSerial, metadataTitle, metadataBranch, metadataGitHash, metadataLastCommitMsg);
            setSavedMetadataInfo(metadataInfo);
            setSavedMetadataSerial(metadataSerial);
            setSavedMetadataTitle(metadataTitle);
            setSavedMetadataBranch(metadataBranch);
            setSavedMetadataGitHash(metadataGitHash);
            setSavedMetadataLastCommitMsg(metadataLastCommitMsg);
            statusContext.postInfo('Metadata saved');
        } catch (err: any) {
            statusContext.postError(`Failed to save metadata: ${err.message}`);
        }
    }

    const changedStyle = (changed: boolean): React.CSSProperties => changed ? {
        borderLeft: `3px solid ${theme?.colors?.info || '#0dcaf0'}`,
        paddingLeft: '0.75rem'
    } : {};

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-4" style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Metadata</h3>
                    <div className="text-muted">Library serial and notes for this database and exported backups.</div>
                </div>
                {dirty && (
                    <Stack direction="horizontal" gap={2}>
                        <Button variant="outline-secondary" onClick={() => {
                            setMetadataInfo(savedMetadataInfo);
                            setMetadataSerial(savedMetadataSerial);
                            setMetadataTitle(savedMetadataTitle);
                            setMetadataBranch(savedMetadataBranch);
                            setMetadataGitHash(savedMetadataGitHash);
                            setMetadataLastCommitMsg(savedMetadataLastCommitMsg);
                        }}>Clear</Button>
                        <Button variant="primary" onClick={save}>Save</Button>
                    </Stack>
                )}
            </div>
            <div className="mb-4" style={changedStyle(metadataSerial !== savedMetadataSerial)}>
                <Form.Label htmlFor="metadataSerial" className="fw-semibold">Serial</Form.Label>
                <div className="text-muted small mb-2">Version or release ID for this working database.</div>
                <Form.Control
                    id="metadataSerial"
                    name="metadataSerial"
                    value={metadataSerial}
                    onChange={(e) => setMetadataSerial(e.target.value)}
                    type="text"
                    style={{ maxWidth: '28rem' }}
                />
            </div>
            <div className="mb-4" style={changedStyle(metadataInfo !== savedMetadataInfo)}>
                <Form.Label htmlFor="metadataNotes" className="fw-semibold">Notes</Form.Label>
                <div className="text-muted small mb-2">Operational context for teammates reviewing saved files and merge candidates.</div>
                <Form.Control
                    as="textarea"
                    id="metadataNotes"
                    name="metadataNotes"
                    value={metadataInfo}
                    onChange={(e) => setMetadataInfo(e.target.value)}
                    rows={7}
                    style={{ maxWidth: '52rem' }}
                />
            </div>
            <div className="mb-4" style={changedStyle(metadataTitle !== savedMetadataTitle)}>
                <Form.Label htmlFor="metadataTitle" className="fw-semibold">Title</Form.Label>
                <div className="text-muted small mb-2">Display title for this database.</div>
                <Form.Control
                    id="metadataTitle"
                    name="metadataTitle"
                    value={metadataTitle}
                    onChange={(e) => setMetadataTitle(e.target.value)}
                    type="text"
                    style={{ maxWidth: '28rem' }}
                />
            </div>
            <div className="mb-4" style={changedStyle(metadataBranch !== savedMetadataBranch)}>
                <Form.Label htmlFor="metadataBranch" className="fw-semibold">Git Branch</Form.Label>
                <div className="text-muted small mb-2">Git branch name for reference (auto-populated on import).</div>
                <Form.Control
                    id="metadataBranch"
                    name="metadataBranch"
                    value={metadataBranch}
                    onChange={(e) => setMetadataBranch(e.target.value)}
                    type="text"
                    style={{ maxWidth: '28rem' }}
                />
            </div>
            <div className="mb-4" style={changedStyle(metadataGitHash !== savedMetadataGitHash)}>
                <Form.Label htmlFor="metadataGitHash" className="fw-semibold">Git Hash</Form.Label>
                <div className="text-muted small mb-2">Git commit hash for reference (auto-populated on import).</div>
                <Form.Control
                    id="metadataGitHash"
                    name="metadataGitHash"
                    value={metadataGitHash}
                    onChange={(e) => setMetadataGitHash(e.target.value)}
                    type="text"
                    style={{ maxWidth: '52rem' }}
                />
            </div>
            <div className="mb-4" style={changedStyle(metadataLastCommitMsg !== savedMetadataLastCommitMsg)}>
                <Form.Label htmlFor="metadataLastCommitMsg" className="fw-semibold">Last Commit Message</Form.Label>
                <div className="text-muted small mb-2">Last git commit message for reference (auto-populated on import).</div>
                <Form.Control
                    as="textarea"
                    id="metadataLastCommitMsg"
                    name="metadataLastCommitMsg"
                    value={metadataLastCommitMsg}
                    onChange={(e) => setMetadataLastCommitMsg(e.target.value)}
                    rows={3}
                    style={{ maxWidth: '52rem' }}
                />
            </div>
        </div>
    );
}

export default MetadataSettings;
